import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import {
  REVIEW_ACTION_LABELS,
  REVIEW_DECISION_LABELS,
  closesReview,
  type ReviewAction,
  type ReviewDecision,
} from '@tinypro-audit/contracts';
import { formatDateTimeInAppTimeZone } from '@tinypro-audit/shared/time';

export function ReviewDecisionEditor({
  title = 'Review decision',
  decision,
  verified = false,
  scope,
  scopeDetails,
  disabledReason,
  correctionHelp,
  children,
  onSave,
  revision,
  canReopen,
}: {
  title?: string;
  decision: ReviewDecision | null;
  revision?: string;
  canReopen?: boolean;
  verified?: boolean;
  scope: string;
  scopeDetails?: React.ReactNode;
  disabledReason?: string;
  correctionHelp: string;
  children?: React.ReactNode;
  onSave: (action: ReviewAction, note: string) => Promise<void>;
}) {
  const [editingRevision, setEditingRevision] = useState(revision);
  const [action, setAction] = useState<ReviewAction | null>(null);
  const [note, setNote] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [saved, setSaved] = useState(false);
  const actions: ReviewAction[] = [
    'not_issue',
    'needs_correction',
    'corrected',
  ];
  if (decision || verified || canReopen) actions.push('reopen');
  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={600}>{title}</Text>
        <Badge
          color={
            verified ? 'teal' : closesReview(decision?.action) ? 'blue' : 'gray'
          }
          variant="light"
        >
          {verified
            ? 'Verified after recheck'
            : decision
              ? REVIEW_DECISION_LABELS[decision.action]
              : 'Needs review'}
        </Badge>
      </Group>
      <Text size="sm" c="dimmed">
        {scope}
      </Text>
      {scopeDetails}
      {decision && (
        <div>
          <Text size="sm">{decision.note}</Text>
          <Text size="xs" c="dimmed">
            {decision.actor_name ?? 'Reviewer'} ·{' '}
            {formatDateTimeInAppTimeZone(decision.created_at)}
          </Text>
        </div>
      )}
      {decision?.action === 'correction_recorded' && !verified && (
        <Text size="sm" c="dimmed">
          This earlier correction was recorded without a final sign-off. Choose
          Correction made once you have fixed and checked it.
        </Text>
      )}
      {disabledReason && (
        <Text size="sm" c="dimmed">
          {disabledReason}
        </Text>
      )}
      <Group gap="xs">
        {actions.map((value) => (
          <Button
            key={value}
            size="xs"
            variant={action === value ? 'filled' : 'default'}
            disabled={Boolean(disabledReason) || busy}
            aria-pressed={action === value}
            onClick={() => {
              setEditingRevision(revision);
              setAction(value);
              setNote('');
              setError('');
              setSaved(false);
            }}
          >
            {REVIEW_ACTION_LABELS[value]}
          </Button>
        ))}
      </Group>
      {action && (
        <Stack gap="sm">
          <Text size="sm">
            {action === 'not_issue'
              ? 'Explain why this finding does not need action. The original analysis stays available.'
              : action === 'corrected'
                ? correctionHelp
                : action === 'waiting'
                  ? 'Record the clarification, correction or response you are waiting for.'
                  : action === 'reopen'
                    ? 'Explain why this item needs another review.'
                    : 'Describe what needs correcting.'}
          </Text>
          {children}
          <Textarea
            label={
              action === 'not_issue'
                ? 'Reason'
                : action === 'corrected'
                  ? 'What did you fix and check?'
                  : 'Review note'
            }
            required
            autosize
            minRows={2}
            maxLength={4000}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <Group>
            <Button
              size="sm"
              loading={busy}
              disabled={!note.trim() || Boolean(disabledReason)}
              onClick={async () => {
                setBusy(true);
                setError('');
                try {
                  if (editingRevision !== revision)
                    throw new Error(
                      'The review or selection changed. Select an action again before saving.',
                    );
                  await onSave(action, note);
                  setAction(null);
                  setNote('');
                  setSaved(true);
                } catch (e) {
                  setError(
                    e instanceof Error
                      ? e.message
                      : 'Could not save the decision.',
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save decision
            </Button>
            <Button
              size="sm"
              variant="subtle"
              disabled={busy}
              onClick={() => {
                setAction(null);
                setError('');
              }}
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      )}
      {error && <Alert color="red">{error}</Alert>}
      {saved && (
        <Text size="sm" c="teal">
          Decision saved.
        </Text>
      )}
    </Stack>
  );
}
